/**
 * Smart Asset Card
 *
 * Interactive card component for displaying Smart Assets with selection behavior.
 * - Single click: Selects the card and shows "Open" button
 * - Double click: Opens the Smart Asset Master Container view
 * - Click outside or on another card: Deselects
 *
 * @see https://gitlab.com/smart-assets.io/smart-asset-primitives/gitlab-profile/-/blob/master/docs/SmartAssetSpec/wireframes/SA_Master_Container_Design.png
 */
import React, { useState, useRef, useCallback } from "react";
import { DESIGNATION_STYLES } from "../../../types/designation";
import type { FeatureDesignation } from "../../../types/designation";
import DesignationWrapper from "../../common/DesignationWrapper";

export interface SmartAssetData {
  id: string;
  name: string;
  ticker: string;
  description: string;
  category: "primitive" | "composite";
  status: "demo" | "prototype" | "alpha" | "active" | "deprecated";
  icon?: React.ReactNode;
  /** Path to icon image (relative to public folder, e.g., "/assets/smart-assets/GET.png") */
  iconPath?: string;
  iconBgClass?: string;
  iconTextClass?: string;
  /** Path to Value Flows diagram image (e.g., "/assets/smart-assets/value-flows/SampleSmartTreasury.png") */
  valueFlowsDiagram?: string;
  /** Whether this Smart Asset's units are transferrable (default: true). Non-transferrable units do not appear in the Cryptocurrencies tab. */
  unitsTransferrable?: boolean;
  /** Whether this Smart Asset supports governance (quadratic voting, proposals, delegation) */
  governanceEnabled?: boolean;
  /** Governance configuration for enabled assets */
  governanceConfig?: {
    /** Voting multiplier based on holder class (default: 1.0) */
    votingMultiplier?: number;
    /** Class label (e.g., "Contributor", "Financier", "Founder") */
    holderClass?: string;
    /** Minimum holding period (days) to participate in governance */
    minHoldingPeriod?: number;
    /** Minimum units required to create proposals */
    proposalThreshold?: number;
    /** Supervoting rules defining all holder classes and their voting multipliers */
    supervotingRules?: Array<{
      /** Holder class name (e.g., "Founder", "Contributor", "Seed Financier") */
      holderClass: string;
      /** Voting power multiplier (e.g., 3.0 for 3x voting power) */
      multiplier: number;
      /** Color for chart visualization (hex or Tailwind class) */
      color: string;
    }>;
  };
}

interface SmartAssetCardProps {
  asset: SmartAssetData;
  onOpen: (assetId: string) => void;
  isSelected?: boolean;
  onSelect?: (assetId: string | null) => void;
  className?: string;
}

const statusBadgeStyles: Record<
  SmartAssetData["status"],
  { bg: string; text: string; label: string }
> = {
  // Designation tiers (from DESIGNATION_STYLES)
  demo: DESIGNATION_STYLES.demo,
  prototype: DESIGNATION_STYLES.prototype,
  alpha: DESIGNATION_STYLES.alpha,
  // Operational states (not designation tiers)
  active: {
    bg: "bg-sa-success/10",
    text: "text-sa-success",
    label: "Active",
  },
  deprecated: {
    bg: "bg-sa-surface-hover",
    text: "text-sa-text",
    label: "Deprecated",
  },
};

/** Extract FeatureDesignation from SmartAssetData.status (returns null for operational states) */
function statusToDesignation(status: SmartAssetData["status"]): FeatureDesignation | null {
  if (status === "active" || status === "deprecated") return null;
  return status as FeatureDesignation;
}

const SmartAssetCard: React.FC<SmartAssetCardProps> = ({
  asset,
  onOpen,
  isSelected = false,
  onSelect,
  className = "",
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [lastClickTime, setLastClickTime] = useState(0);
  const DOUBLE_CLICK_THRESHOLD = 300; // ms

  const statusStyle = statusBadgeStyles[asset.status];

  // Handle click - toggle selection or double-click to open
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const now = Date.now();

      if (now - lastClickTime < DOUBLE_CLICK_THRESHOLD) {
        // Double click - open the asset
        onOpen(asset.id);
        setLastClickTime(0);
      } else {
        // Single click - select/deselect
        if (onSelect) {
          onSelect(isSelected ? null : asset.id);
        }
        setLastClickTime(now);
      }
    },
    [asset.id, isSelected, lastClickTime, onOpen, onSelect],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        onOpen(asset.id);
      } else if (e.key === " ") {
        e.preventDefault();
        if (onSelect) {
          onSelect(isSelected ? null : asset.id);
        }
      }
    },
    [asset.id, isSelected, onOpen, onSelect],
  );

  // Handle Open button click
  const handleOpenClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpen(asset.id);
    },
    [asset.id, onOpen],
  );

  const designation = statusToDesignation(asset.status);

  return (
    <DesignationWrapper designation={designation} sizeHint="inline">
      <div
        ref={cardRef}
        data-testid={`smart-asset-card-${asset.id}`}
        data-asset-id={asset.id}
        data-selected={isSelected}
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`
        relative p-4 border rounded-lg shadow-sm transition-all duration-200 cursor-pointer
        ${
          isSelected
            ? "border-purple-500 bg-purple-500/10 ring-2 ring-purple-500/50"
            : "border-sa-border bg-sa-surface-hover hover:border-sa-border-hover"
        }
        ${className}
      `}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            {/* Icon */}
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center mr-3 overflow-hidden ${
                asset.iconPath ? "" : asset.iconBgClass || "bg-purple-500/10"
              }`}
            >
              {asset.iconPath ? (
                <img
                  src={asset.iconPath}
                  alt={`${asset.name} icon`}
                  className="w-full h-full object-cover"
                />
              ) : asset.icon ? (
                asset.icon
              ) : (
                <span
                  className={`font-semibold text-sm ${asset.iconTextClass || "text-purple-500"}`}
                >
                  {asset.ticker.slice(0, 2)}
                </span>
              )}
            </div>

            {/* Name and Category */}
            <div>
              <p className="text-sm font-semibold text-sa-text">{asset.name}</p>
              <p className="text-xs text-sa-text-muted">
                Smart Asset | {asset.category === "primitive" ? "Primitive" : "Composite"}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-md ${statusStyle.bg} ${statusStyle.text}`}
          >
            {statusStyle.label}
          </span>
        </div>

        {/* Description */}
        <p className="text-xs text-sa-text-muted leading-snug">{asset.description}</p>

        {/* Open Button - shown when selected */}
        {isSelected && (
          <div className="absolute inset-0 flex items-end justify-center pb-6 bg-black/10 rounded-lg">
            <button
              data-testid={`smart-asset-open-${asset.id}`}
              onClick={handleOpenClick}
              className="px-6 py-2 bg-primary hover:bg-purple-500 text-white font-semibold rounded-lg shadow-lg transition-colors"
            >
              Open
            </button>
          </div>
        )}

        {/* Double-click hint when selected */}
        {isSelected && (
          <div className="absolute bottom-1 right-2 text-[10px] text-purple-500">
            Double-click to open
          </div>
        )}
      </div>
    </DesignationWrapper>
  );
};

export default SmartAssetCard;
